<script lang="ts">
  import ProgressStepsContent from "@kontextwerk/web-sdk/ui/navigation/ProgressStepsContent.svelte"
  import { onMount } from "svelte"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/core/navigation/progressSteps"
  import {
    firstCurrentWorkflowStep,
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../_shared/_logic/workflow-progress"
  import type {
    AppointmentCreateInit,
    AppointmentCreateOutputs,
    AppointmentCreateStepId,
    AppointmentCreateWorkflowEvent,
    OverlayHostProps,
  } from "../../_logic/create-appointment-contract"
  import {
    applyAppointmentCreateWorkflowEvent,
    nextAppointmentCreateStep,
  } from "./_logic/transitions"
  import GeneralStep from "./steps/a_general"
  import PlanningStep from "./steps/b_planning"
  import AnnouncementStep from "./steps/c_announcement"

  type Props = {
    init?: AppointmentCreateInit | null
    organizationId: string
    employees: AppointmentCreateInit["employees"]
    workOrders: WorkOrder[]
    properties: Property[]
    customers?: Customer[]
    contactPoints?: ContactPoint[]
    scheduleEvents?: ScheduleEvent[]
    workingHoursByUser?: AppointmentCreateInit["workingHoursByUser"]
    candidateEmployeeIds?: string[]
    defaultEmployeeId?: string | null
    defaultStart?: Date | null
    defaultEnd?: Date | null
    defaultFlexType?: ScheduleEventFlexType | null
    defaultStatus?: ScheduleEventStatus | null
    defaultWorkOrderId?: string | null
    defaultPropertyId?: string | null
    flexSlotMinutes?: number | null
    flexSlotsPerDay?: number | null
    allowFreeAppointment?: boolean
    allowWorkOrderSelection?: boolean
    allowNewWorkOrder?: boolean
    defaultWorkOrderSource?: AppointmentCreateInit["defaultWorkOrderSource"]
    orderTypes?: OrderType[]
    defaultOrderNumber?: string | null
    onCreated?: (event: ScheduleEvent) => Promise<void> | void
    onSwitchToBlocker?: (() => Promise<void> | void) | null
  } & OverlayHostProps

  let {
    init: providedInit = null,
    organizationId,
    employees = [],
    workOrders = [],
    properties = [],
    customers = [],
    contactPoints = [],
    scheduleEvents = [],
    workingHoursByUser = {},
    candidateEmployeeIds = [],
    defaultEmployeeId = null,
    defaultStart = null,
    defaultEnd = null,
    defaultFlexType = "HARD",
    defaultStatus = "RESERVED",
    defaultWorkOrderId = null,
    defaultPropertyId = null,
    flexSlotMinutes = null,
    flexSlotsPerDay = null,
    allowFreeAppointment = false,
    allowWorkOrderSelection = true,
    allowNewWorkOrder = false,
    defaultWorkOrderSource = null,
    orderTypes = [],
    defaultOrderNumber = null,
    onCreated = async () => {},
    onSwitchToBlocker = null,
    onremoveOverlay = () => {},
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props = $props()

  const workflowInit: AppointmentCreateInit = $derived(
    providedInit ?? {
      organizationId,
      employees,
      workOrders,
      properties,
      customers,
      contactPoints,
      scheduleEvents,
      workingHoursByUser,
      candidateEmployeeIds,
      defaultEmployeeId,
      defaultStart,
      defaultEnd,
      defaultFlexType: defaultFlexType ?? "HARD",
      defaultStatus: defaultStatus ?? "RESERVED",
      defaultWorkOrderId,
      defaultPropertyId,
      flexSlotMinutes,
      flexSlotsPerDay,
      allowFreeAppointment,
      allowWorkOrderSelection,
      allowNewWorkOrder,
      defaultWorkOrderSource,
      orderTypes,
      defaultOrderNumber,
    },
  )

  const stepOrder = ["general", "planning", "announcement"] as const

  let selectedStep = $state<AppointmentCreateStepId>("general")
  let outputs = $state<AppointmentCreateOutputs>({})
  let stepError = $state<string | null>(null)

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)

    return () => setActionButtonSnippet?.(null)
  })

  const setStep = (step: AppointmentCreateStepId) => {
    stepError = null
    selectedStep = step
  }

  const setStepError = (message: string | null) => {
    stepError = message
  }

  async function applyWorkflowEvent(event: AppointmentCreateWorkflowEvent) {
    if (event.type === "switch_to_blocker") {
      onremoveOverlay?.()
      await onSwitchToBlocker?.()

      return
    }

    outputs = applyAppointmentCreateWorkflowEvent(outputs, event)
    setStep(nextAppointmentCreateStep(currentStep ?? selectedStep, event))

    if (event.type === "appointment.created") {
      await onCreated(event.event)
      onremoveOverlay?.()
    }
  }

  const completedSteps = $derived.by(() => {
    const completed: AppointmentCreateStepId[] = []

    if (outputs.general) completed.push("general")

    if (outputs.planning) completed.push("planning")

    return completed
  })
  const currentStep = $derived(
    firstCurrentWorkflowStep<AppointmentCreateStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const selectedIndex = $derived(
    selectedWorkflowStepIndex<AppointmentCreateStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const handleSelectedIndexChange = (index: number) => {
    const next = stepOrder[index]

    if (next) setStep(next)
  }

  const progressSteps: ProgressStep[] = $derived([
    {
      id: "general",
      label: "Allgemein",
      workflowState: workflowStateForStep({
        step: "general",
        currentStep,
        completedSteps,
      }),
      snippet: generalStep,
    },
    {
      id: "planning",
      label: "Planung",
      workflowState: workflowStateForStep({
        step: "planning",
        currentStep,
        completedSteps,
      }),
      snippet: planningStep,
    },
    {
      id: "announcement",
      label: "Ankündigung",
      workflowState: workflowStateForStep({
        step: "announcement",
        currentStep,
        completedSteps,
      }),
      snippet: announcementStep,
    },
  ])
</script>

{#snippet generalStep(ctx: ProgressStepContext)}
  <GeneralStep
    init={workflowInit}
    output={outputs.general}
    readOnly={ctx.readOnly}
    canSwitchToBlocker={Boolean(onSwitchToBlocker)}
    onEvent={applyWorkflowEvent}
    onError={setStepError}
  />
{/snippet}

{#snippet planningStep(ctx: ProgressStepContext)}
  {#if outputs.general}
    <PlanningStep
      init={workflowInit}
      general={outputs.general}
      output={outputs.planning}
      readOnly={ctx.readOnly}
      onBack={() => setStep("general")}
      onEvent={applyWorkflowEvent}
      onError={setStepError}
    />
  {/if}
{/snippet}

{#snippet announcementStep(ctx: ProgressStepContext)}
  {#if outputs.general && outputs.planning}
    <AnnouncementStep
      init={workflowInit}
      general={outputs.general}
      planning={outputs.planning}
      output={outputs.announcement}
      readOnly={ctx.readOnly}
      onBack={() => setStep("planning")}
      onWorkOrderCreated={(output) =>
        applyWorkflowEvent({
          type: "general.work_order_created",
          output,
        })}
      {onCreated}
      onClose={onremoveOverlay}
      onError={setStepError}
    />
  {/if}
{/snippet}

<div class="create-appointment-overlay form light">
  <ProgressSteps
    steps={progressSteps}
    showControls={false}
    darkMode={true}
    syncUrl={false}
    statusMode="explicit"
    navigationMode="guarded"
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
  />
  {#if stepError}
    <ProgressStepsContent>
      <div class="step-error">{stepError}</div>
    </ProgressStepsContent>
  {/if}
</div>

<style>
  .create-appointment-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .step-error {
    border: 1px solid var(--error-100);
    color: var(--error-100);
    padding: 0.75rem;
  }
</style>
